import React, { useCallback, useState } from "react";

import { t } from "@lingui/core/macro";
import { noop } from "lodash/fp";

import TooltipWrapper from "@readai/common/components/TooltipWrapper";
import useCopyToClipboard from "@readai/common/hooks/useCopyToClipboard";
import CheckIcon from "@readai/icons/uui/Check";

import CopyDialog from "./CopyDialog";

interface CommonProps {
  copyValue: string;
  copyHtmlValue?: string;
  copyTimeout?: number;
  onClick?: (copied: boolean) => void;
  stopPropagation?: boolean;
  tooltip?: string;
  tooltipCopied?: string;
}

interface CopyIconButtonProps extends CommonProps {
  renderIconButton: (
    buttonProps: {
      onClick: (event: React.MouseEvent<HTMLElement>) => void;
      children: React.ReactNode;
    },
    copied: boolean,
  ) => React.ReactElement;
}

interface CopyStandardButtonProps extends CommonProps {
  renderButton: (
    buttonProps: {
      onClick: (event: React.MouseEvent<HTMLElement>) => void;
    },
    copied: boolean,
  ) => React.ReactElement;
}

type CopyButtonProps = CopyIconButtonProps | CopyStandardButtonProps;

const CopyButton = ({
  copyValue,
  copyHtmlValue,
  copyTimeout,
  onClick = noop,
  stopPropagation = false,
  tooltip,
  tooltipCopied = t`Copied!`,
  ...props
}: CopyButtonProps) => {
  const { copied, copyToClipboard, isSupported } =
    useCopyToClipboard(copyTimeout);

  const [open, setOpen] = useState(false);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }

      onClick(isSupported);
      if (isSupported) {
        copyToClipboard(copyValue, copyHtmlValue);
      } else {
        setOpen(true);
      }
    },
    [copyValue, onClick, copyToClipboard, isSupported, stopPropagation],
  );

  let Component;

  if ("renderIconButton" in props) {
    Component = props.renderIconButton(
      {
        onClick: handleClick,
        children: copied ? (
          <CheckIcon
            className="copied-icon"
            sx={{
              color: (theme) => theme.palette.icon.indicator.success.default,
            }}
          />
        ) : undefined,
      },
      copied,
    );
  } else {
    Component = props.renderButton({ onClick: handleClick }, copied);
  }

  if (tooltip) {
    Component = (
      <TooltipWrapper
        placement="bottom"
        title={copied ? tooltipCopied : tooltip}
        enterDelay={copied ? 0 : 1000}
      >
        {Component}
      </TooltipWrapper>
    );
  }

  if (isSupported) {
    return Component ?? null;
  } else {
    return (
      <>
        <CopyDialog
          copyValue={copyValue}
          open={open}
          onClose={() => setOpen(false)}
        />
        {Component}
      </>
    );
  }
};

export default CopyButton;
